import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

interface SubMdrItem {
  id: string;
  displayName: string;
}
interface MerchantItem {
  merchantId: string;
  code: string;
  name: string;
  masterPayinFeePercent: number;
  masterPayoutFeePercent: number;
}
interface GroupItem {
  id: string;
  name: string;
}
interface AssignmentItem {
  id: string;
  subMdrId: string;
  merchantId: string;
  payinFeePercent: number;
  payoutFeePercent: number;
}

export function MdrAssignments() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [subMdrId, setSubMdrId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [merchantIds, setMerchantIds] = useState<Set<string>>(new Set());
  const [payinFeePercent, setPayinFeePercent] = useState('0');
  const [payoutFeePercent, setPayoutFeePercent] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleCollapse = (sid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });

  const { data: subsData } = useQuery({
    queryKey: ['mdr', 'sub-mdrs'],
    queryFn: () => apiGet<{ items: SubMdrItem[] }>('/mdr/sub-mdrs'),
  });
  const { data: merchantsData } = useQuery({
    queryKey: ['mdr', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[] }>('/mdr/merchants'),
  });
  const { data: groupsData } = useQuery({
    queryKey: ['mdr', 'groups'],
    queryFn: () => apiGet<{ items: GroupItem[] }>('/mdr/groups'),
  });
  const { data: assignmentsData } = useQuery({
    queryKey: ['mdr', 'assignments', subMdrId],
    queryFn: () =>
      apiGet<{ items: AssignmentItem[] }>(
        '/mdr/assignments',
        subMdrId ? { subMdrId } : undefined,
      ),
  });

  const subs = subsData?.ok ? subsData.data.items : [];
  const merchants = merchantsData?.ok ? merchantsData.data.items : [];
  const groups = groupsData?.ok ? groupsData.data.items : [];
  const assignments = assignmentsData?.ok ? assignmentsData.data.items : [];
  const merchantById = new Map(merchants.map((m) => [m.merchantId, m]));
  const subById = new Map(subs.map((s) => [s.id, s.displayName]));

  // Group assignments by sub MDR (sorted by name) for display.
  const groupedAssignments = (() => {
    const map = new Map<string, AssignmentItem[]>();
    for (const a of assignments) {
      const arr = map.get(a.subMdrId) ?? [];
      arr.push(a);
      map.set(a.subMdrId, arr);
    }
    return [...map.entries()]
      .map(([sid, items]) => ({ sid, name: subById.get(sid) ?? sid, items }))
      .sort((a, b) => a.name.localeCompare(b.name));
  })();

  const assignMutation = useMutation({
    mutationFn: () =>
      apiPost<{ assigned: number }>('/mdr/assignments', {
        subMdrId,
        groupId: groupId || undefined,
        merchantIds: merchantIds.size > 0 ? [...merchantIds] : undefined,
        payinFeePercent: Number(payinFeePercent),
        payoutFeePercent: Number(payoutFeePercent),
      }),
    onSuccess: (res) => {
      if (res.ok) {
        setOkMsg(t('mdr.assignedCount', { count: res.data.assigned }));
        setError(null);
        setMerchantIds(new Set());
        setGroupId('');
        queryClient.invalidateQueries({ queryKey: ['mdr', 'assignments'] });
      } else {
        setError(res.error.message);
        setOkMsg(null);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/mdr/assignments/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mdr', 'assignments'] }),
  });

  const toggle = (id: string) =>
    setMerchantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
        {t('mdr.assignmentsTitle')}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('mdr.assignSub')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-gray-500">{t('mdr.subMdr')}</span>
              <select
                value={subMdrId}
                onChange={(e) => setSubMdrId(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="">{t('mdr.selectSub')}</option>
                {subs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-gray-500">{t('mdr.group')}</span>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                <option value="">{t('mdr.orPickMerchants')}</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!groupId && (
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
              {merchants.map((m) => (
                <label key={m.merchantId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={merchantIds.has(m.merchantId)}
                    onChange={() => toggle(m.merchantId)}
                  />
                  <span className="font-mono text-xs text-gray-500">{m.code}</span>
                  <span className="flex-1 truncate">{m.name}</span>
                  <span className="text-xs text-gray-400">
                    ≤ {m.masterPayinFeePercent}% / {m.masterPayoutFeePercent}%
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-gray-500">{t('mdr.payinFee')} (%)</span>
              <Input
                type="number"
                step="0.000001"
                value={payinFeePercent}
                onChange={(e) => setPayinFeePercent(e.target.value)}
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-gray-500">{t('mdr.payoutFee')} (%)</span>
              <Input
                type="number"
                step="0.000001"
                value={payoutFeePercent}
                onChange={(e) => setPayoutFeePercent(e.target.value)}
              />
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
          {okMsg && <p className="text-sm text-green-600">{okMsg}</p>}

          <Button
            size="sm"
            disabled={!subMdrId || assignMutation.isPending}
            onClick={() => assignMutation.mutate()}
          >
            {t('mdr.assign')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('mdr.currentAssignments')} ({assignments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 p-0 pb-2">
          {groupedAssignments.length === 0 ? (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          ) : (
            groupedAssignments.map((grp) => (
              <div key={grp.sid}>
                <button
                  type="button"
                  onClick={() => toggleCollapse(grp.sid)}
                  className="flex w-full items-center justify-between border-b border-gray-100 bg-gray-50/70 px-5 py-2.5 text-left transition-colors hover:bg-gray-100 dark:border-gray-700/60 dark:bg-gray-900/30 dark:hover:bg-gray-800/60"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                    <span className="text-gray-400">{collapsed.has(grp.sid) ? '▸' : '▾'}</span>
                    {grp.name}
                  </span>
                  <span className="text-xs text-gray-400">
                    {t('mdr.merchantCount')}: {grp.items.length}
                  </span>
                </button>
                {!collapsed.has(grp.sid) && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('merchants.name')}</TableHead>
                        <TableHead className="text-right">{t('mdr.payinFee')}</TableHead>
                        <TableHead className="text-right">{t('mdr.payoutFee')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grp.items.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            {merchantById.get(a.merchantId)?.name ?? a.merchantId}
                          </TableCell>
                          <TableCell className="text-right">{a.payinFeePercent}%</TableCell>
                          <TableCell className="text-right">{a.payoutFeePercent}%</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteMutation.mutate(a.id)}
                            >
                              {t('common.delete')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
